import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import CompanyLogo from './CompanyLogo';
import EntityPicker from './EntityPicker';
import { CreateTaskModal } from './TasksPanel';

// Stage list is now fetched from /api/deal-stages on mount. Fallback list
// matches the seed data in db.py — only used until the API responds, so
// the kanban renders something instead of empty space on slow connections.
const FALLBACK_STAGES = [
  { id: 'prospecting',  label: 'Prospecting',  color: '#6366f1', probability: 10,  is_lost: false, is_won: false, in_funnel: true },
  { id: 'outreach',     label: 'Outreach',     color: '#8b5cf6', probability: 25,  is_lost: false, is_won: false, in_funnel: true },
  { id: 'demo',         label: 'Demo',         color: '#a855f7', probability: 45,  is_lost: false, is_won: false, in_funnel: true },
  { id: 'proposal',     label: 'Proposal',     color: '#f97316', probability: 70,  is_lost: false, is_won: false, in_funnel: true },
  { id: 'negotiation',  label: 'Negotiation',  color: '#eab308', probability: 85,  is_lost: false, is_won: false, in_funnel: true },
  { id: 'nurture',      label: 'Nurture',      color: '#0ea5e9', probability: 20,  is_lost: false, is_won: false, in_funnel: false },
  { id: 'closed_won',   label: 'Closed Won',   color: '#22c55e', probability: 100, is_lost: false, is_won: true,  in_funnel: true },
  { id: 'closed_lost',  label: 'Closed Lost',  color: '#ef4444', probability: 0,   is_lost: true,  is_won: false, in_funnel: false },
];

const fmt = (n) => {
  if (!n) return '$0';
  return '$' + Number(n).toLocaleString();
};

// Where a deal originated. Fixed pick-list — the DB column is free-text
// so this can be extended without a migration.
const DEAL_SOURCES = ['Inbound', 'Prospecting', 'Referral', 'Event', 'Partner', 'Other'];

// Convert an ISO timestamp into "3d ago" / "2h ago" / "just now". Keeps the
// kanban cards scannable — sales reps care about recency more than wall time.
function timeAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60)            return 'just now';
  if (s < 3600)          return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)         return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 14)    return `${Math.floor(s / 86400)}d ago`;
  if (s < 86400 * 60)    return `${Math.floor(s / 86400 / 7)}w ago`;
  return `${Math.floor(s / 86400 / 30)}mo ago`;
}

function ageColor(iso, stage) {
  // Stale-deal warning colors. Different thresholds per stage — a deal sitting
  // in prospecting for 2 weeks is fine, but stuck in negotiation that long
  // probably needs a poke.
  if (!iso) return 'text-slate-500';
  const days = (Date.now() - new Date(iso).getTime()) / 86400000;
  const stale = stage === 'negotiation' || stage === 'proposal' ? 5 : 14;
  const cold  = stage === 'negotiation' || stage === 'proposal' ? 10 : 30;
  if (days > cold)  return 'text-red-400';
  if (days > stale) return 'text-amber-400';
  return 'text-slate-500';
}

function DealCard({ deal, onEdit, onDragStart, ownerLabel = (x) => x }) {
  const lastActivity = deal.last_activity_at || deal.updated_at;
  const ago = timeAgo(lastActivity);
  // Preview the freshest logged note/follow-up, falling back to the legacy
  // notes column for deals that predate activity logging — previewing
  // deal.notes alone showed stale text once reps started using Log Note.
  const notesPreview = (deal.latest_note || deal.notes || '').trim();
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('deal_id', deal.id);
        onDragStart?.(deal.id);
      }}
      onClick={() => onEdit(deal)}
      className="bg-slate-800 border border-slate-600 rounded-lg p-3 cursor-pointer hover:border-indigo-500 transition-all mb-2"
    >
      <div className="font-medium text-sm text-slate-100 truncate">{deal.name}</div>
      {deal.company_name && (
        <div className="text-xs text-slate-400 mt-1 truncate">{deal.company_name}</div>
      )}
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-semibold text-emerald-400">{fmt(deal.amount)}</span>
        {deal.close_date && (
          <span className="text-xs text-slate-500">{deal.close_date.split('T')[0]}</span>
        )}
      </div>
      {notesPreview && (
        <div className="text-xs text-slate-400 mt-2 line-clamp-2 whitespace-pre-wrap leading-snug">
          {notesPreview.length > 140 ? notesPreview.slice(0, 140) + '…' : notesPreview}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 gap-2">
        {deal.owner && (
          <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full truncate max-w-[60%]"
                title={deal.owner}>
            {ownerLabel(deal.owner)}
          </span>
        )}
        {ago && (
          <span className={'text-[10px] tabular-nums ' + ageColor(lastActivity, deal.stage)}>
            {ago}
          </span>
        )}
      </div>
    </div>
  );
}

function KanbanColumn({
  stage, deals, onEdit, onDrop, onDragStart, dragOverStage, setDragOverStage,
  collapsed, onToggleCollapse, sortKey, ownerLabel,
}) {
  // Per-column search box — applied client-side over name / company / contact / owner.
  const [columnQuery, setColumnQuery] = useState('');
  const stageDealsAll = deals.filter(d => d.stage === stage.id);

  const q = columnQuery.trim().toLowerCase();
  let stageDeals = q
    ? stageDealsAll.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.company_name || '').toLowerCase().includes(q) ||
        (d.contact_name || '').toLowerCase().includes(q) ||
        (d.owner || '').toLowerCase().includes(q))
    : stageDealsAll;

  // Apply the column's sort key.
  stageDeals = [...stageDeals].sort((a, b) => {
    if (sortKey === 'amount') return (b.amount || 0) - (a.amount || 0);
    if (sortKey === 'oldest') return new Date(a.last_activity_at || a.updated_at || 0) - new Date(b.last_activity_at || b.updated_at || 0);
    /* default: newest activity first */
    return new Date(b.last_activity_at || b.updated_at || 0) - new Date(a.last_activity_at || a.updated_at || 0);
  });

  const total = stageDealsAll.reduce((s, d) => s + (d.amount || 0), 0);
  const weighted = stageDealsAll.reduce((s, d) => s + ((d.amount || 0) * (d.weight ?? stage.probability ?? stage.prob ?? 0) / 100), 0);
  const isOver = dragOverStage === stage.id;

  return (
    <div
      className={`flex-shrink-0 flex flex-col rounded-lg transition-all ${collapsed ? 'w-12' : 'w-72'} ${isOver ? 'ring-2 ring-indigo-500' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
      onDragLeave={() => setDragOverStage(null)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOverStage(null);
        const dealId = e.dataTransfer.getData('deal_id');
        if (dealId) onDrop(dealId, stage.id);
      }}
    >
      {/* Column Header */}
      <div className="px-3 py-2 rounded-t-lg" style={{ backgroundColor: stage.color + '22' }}>
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => onToggleCollapse(stage.id)}
            className="text-slate-400 hover:text-slate-200 text-xs"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '›' : '‹'}
          </button>
          {!collapsed && (
            <>
              <span className="text-sm font-semibold flex-1 truncate" style={{ color: stage.color }}>{stage.label}</span>
              <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">{stageDealsAll.length}</span>
            </>
          )}
          {collapsed && (
            <span className="text-xs text-slate-400 [writing-mode:vertical-rl] mt-1">{stage.label} · {stageDealsAll.length}</span>
          )}
        </div>
        {!collapsed && (
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-400">{fmt(total)}</span>
            <span className="text-xs text-slate-500">Wtd: {fmt(weighted)}</span>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Per-column search */}
          {stageDealsAll.length >= 5 && (
            <div className="px-2 pt-2">
              <input
                type="text"
                value={columnQuery}
                onChange={(e) => setColumnQuery(e.target.value)}
                placeholder={`Filter ${stage.label.toLowerCase()}…`}
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 placeholder-slate-600"
              />
              {q && (
                <div className="text-[10px] text-slate-500 mt-1">{stageDeals.length} of {stageDealsAll.length} match</div>
              )}
            </div>
          )}

          {/* Cards */}
          <div className="flex-1 overflow-y-auto p-2 bg-slate-850 rounded-b-lg min-h-[200px] scrollable-content" style={{ backgroundColor: '#0f172a88' }}>
            {stageDeals.map(deal => (
              <DealCard key={deal.id} deal={deal} onEdit={onEdit} onDragStart={onDragStart} ownerLabel={ownerLabel} />
            ))}
            {stageDeals.length === 0 && (
              <div className="text-center text-xs text-slate-600 py-8">
                {q ? 'No matches' : 'Drop deals here'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DealModal({ deal, stages, lostReasons, users = [], onClose, onSave, onDelete }) {
  const isNew = !deal?.id;
  // Default a NEW deal's stage to the first in-funnel stage from the
  // dynamic stages list — NOT a hardcoded 'prospecting', which may not
  // exist if the admin renamed/removed stages. A select whose value
  // doesn't match any option silently keeps the stale value, which is
  // how deals were landing in a dead 'prospecting' stage and vanishing.
  const _firstFunnelStage = (stages || []).find(s => s.in_funnel && !s.is_won && !s.is_lost)
                          || (stages || [])[0];
  const [form, setForm] = useState({
    name: deal?.name || '',
    stage: deal?.stage || _firstFunnelStage?.id || 'prospecting',
    amount: deal?.amount || '',
    weight: deal?.weight ?? '',
    currency: deal?.currency || 'USD',
    close_date: deal?.close_date?.split('T')[0] || '',
    owner: deal?.owner || '',
    // Notes textarea is a fresh-entry box (clicking Log Note appends to
    // the activity feed). Never pre-populate it — legacy deal.notes will
    // still show in the activity feed below when there's nothing else.
    notes: '',
    source: deal?.source || '',
    loss_reason: deal?.loss_reason || '',
    loss_reason_id: deal?.loss_reason_id || '',
  });

  // Belt-and-suspenders: useState's initializer runs once at mount, so if
  // `stages` arrived a tick later (or the panel was still on FALLBACK_STAGES),
  // form.stage can be locked to a stage id that doesn't exist in the real
  // list. When stages settle, snap a NEW deal's stage onto the first valid
  // funnel stage. Only for new deals — never override a saved deal's stage.
  useEffect(() => {
    if (!isNew || !(stages && stages.length)) return;
    const valid = stages.some(s => s.id === form.stage);
    if (!valid) {
      const first = stages.find(s => s.in_funnel && !s.is_won && !s.is_lost) || stages[0];
      if (first) setForm(f => ({ ...f, stage: first.id }));
    }
  }, [stages, isNew]);

  // Log Note composer must open blank on every deal — clear any unsent text when
  // the deal changes (the modal instance can be reused across deals). Punch-list fix.
  useEffect(() => {
    setForm(f => ({ ...f, notes: '' }));
  }, [deal?.id]);

  // Selected company + primary contact for the deal. These are tracked
  // separately from `form` because they're objects (we keep around id, name,
  // website, etc. so the picker can render a chip + the header can show
  // the logo without an extra fetch). Persisted via company_id and
  // primary_contact_id on save.
  const [selectedCompany, setSelectedCompany] = useState(
    deal?.company_id
      ? {
          id: deal.company_id,
          name: deal.company_name || '',
          website: deal.company_website || null,
          linkedin_url: deal.company_linkedin_url || null,
        }
      : null,
  );
  const [selectedContact, setSelectedContact] = useState(
    deal?.primary_contact_id
      ? { id: deal.primary_contact_id, full_name: deal.contact_name || '', title: deal.contact_title || '' }
      : null,
  );
  // Whether the selected stage is "lost" — drives loss-reason picker visibility.
  const selectedStage = stages.find(s => s.id === form.stage);
  const showLossReason = !!selectedStage?.is_lost;
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [activity, setActivity] = useState([]);
  const [dealTasks, setDealTasks]     = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);

  React.useEffect(() => {
    if (!isNew && deal?.id) {
      loadContacts();
      loadLeads();
      loadActivity();
      loadDealTasks();
    } else {
      loadLeads();
    }
  }, [deal?.id, isNew]);

  const loadDealTasks = async () => {
    if (!deal?.id) { setDealTasks([]); return; }
    try {
      const list = await api.fetch(`/api/tasks?deal_id=${encodeURIComponent(deal.id)}`);
      setDealTasks(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load deal tasks:', err);
      setDealTasks([]);
    }
  };

  const handleToggleTask = async (t) => {
    const next = t.status === 'closed' ? 'open' : 'closed';
    try {
      await api.fetch(`/api/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      await loadDealTasks();
    } catch (err) {
      console.error('Failed to toggle task:', err);
      setSaveError('Failed to update task: ' + (err.message || err));
    }
  };

  const loadContacts = async () => {
    try {
      const data = await api.fetch(`/api/deals/${deal.id}/contacts`);
      // Backend returns { deal_id, contacts: [...] } — unwrap it.
      // Also tolerate older / alternate shapes by falling back gracefully.
      const list = Array.isArray(data) ? data
                 : Array.isArray(data?.contacts) ? data.contacts
                 : [];
      setContacts(list);
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setContacts([]);
    }
  };

  const loadActivity = async () => {
    if (!deal?.id) { setActivity([]); return; }
    try {
      const data = await api.fetch(`/api/deals/${deal.id}/activity`);
      const list = Array.isArray(data) ? data
                 : Array.isArray(data?.activity) ? data.activity
                 : Array.isArray(data?.items) ? data.items
                 : [];
      setActivity(list);
    } catch (err) {
      console.error('Failed to load activity:', err);
      setActivity([]);
    }
  };

  const loadLeads = async () => {
    try {
      const data = await api.getLeads({ limit: 1000 });
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Failed to load leads:', err);
    }
  };

  const handleAddContact = async () => {
    if (!selectedLeadId) return;
    try {
      setSaveError(null);
      await api.addDealContact(deal.id, selectedLeadId);
      setSelectedLeadId('');
      setShowAddContact(false);
      await loadContacts();
    } catch (err) {
      console.error('Failed to add contact:', err);
      setSaveError('Failed to add contact: ' + (err.message || err));
    }
  };

  const handleRemoveContact = async (contactId) => {
    try {
      setSaveError(null);
      await api.removeDealContact(deal.id, contactId);
      await loadContacts();
    } catch (err) {
      console.error('Failed to remove contact:', err);
      setSaveError('Failed to remove contact: ' + (err.message || err));
    }
  };

  const handleSetPrimary = async (contactId) => {
    try {
      setSaveError(null);
      await api.setPrimaryContact(deal.id, contactId);
      await loadContacts();
    } catch (err) {
      console.error('Failed to set primary contact:', err);
      setSaveError('Failed to set primary contact: ' + (err.message || err));
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    if (!form.name || !form.name.trim()) {
      setSaveError('Deal name is required.');
      return;
    }
    if (showLossReason && !form.loss_reason_id) {
      setSaveError('Pick a closed-lost reason before saving.');
      return;
    }
    setSaving(true);
    try {
      // Bulletproof stage guard: if form.stage isn't a real configured
      // stage (stale 'prospecting' default, race on stages load, etc.),
      // snap it to the first in-funnel stage right here. This is what
      // actually gets sent — UI timing can no longer produce a 400.
      let effectiveStage = form.stage;
      if (stages && stages.length && !stages.some(s => s.id === effectiveStage)) {
        const firstStage = stages.find(s => s.in_funnel && !s.is_won && !s.is_lost) || stages[0];
        if (firstStage) effectiveStage = firstStage.id;
      }
      // Only send non-empty optional fields — avoids backend coercion errors
      // when a field is "" for a column that expects a number/date.
      const payload = { name: form.name.trim(), stage: effectiveStage };
      if (form.amount !== '' && form.amount !== null && form.amount !== undefined) {
        payload.amount = Number(form.amount);
      }
      if (form.currency) payload.currency = form.currency;
      // Weight: send the number when set; on update, an emptied field
      // clears the override back to the stage default.
      if (form.weight !== '' && form.weight !== null && form.weight !== undefined) {
        payload.weight = Math.max(0, Math.min(100, Math.round(Number(form.weight))));
      } else if (!isNew && deal?.weight != null) {
        payload.weight = null;
      }
      if (form.close_date) payload.close_date = form.close_date;
      if (form.owner) payload.owner = form.owner;
      if (form.notes) payload.notes = form.notes;
      // Send source on create always (so a cleared value persists as null
      // on update too); on update only when it changed from the loaded deal.
      if (isNew || (form.source || '') !== (deal?.source || '')) {
        payload.source = form.source || null;
      }
      if (form.loss_reason) payload.loss_reason = form.loss_reason;
      if (showLossReason && form.loss_reason_id) {
        payload.loss_reason_id = form.loss_reason_id;
      }
      // Send company_id / primary_contact_id when the rep has chosen them
      // in the picker. On update, sending null is meaningful too (it clears
      // the link), but we only send when the in-memory value has changed
      // from the loaded deal to avoid surprise wipes.
      const originalCompanyId = deal?.company_id || null;
      const originalContactId = deal?.primary_contact_id || null;
      const newCompanyId = selectedCompany?.id || null;
      const newContactId = selectedContact?.id || null;
      if (isNew || newCompanyId !== originalCompanyId) {
        payload.company_id = newCompanyId;
      }
      if (isNew || newContactId !== originalContactId) {
        payload.primary_contact_id = newContactId;
      }

      let createdId = null;
      if (isNew) {
        const r = await api.fetch('/api/deals', { method: 'POST', body: JSON.stringify(payload) });
        createdId = r?.id;
      } else {
        await api.fetch(`/api/deals/${deal.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      }
      // Also attach the primary contact as a deal_contact so it shows up in
      // the contacts list (and so removing it later works as expected).
      const dealIdForContact = isNew ? createdId : deal.id;
      if (dealIdForContact && newContactId && newContactId !== originalContactId) {
        try {
          await api.addDealContact(dealIdForContact, newContactId, 'primary');
        } catch (e) {
          // Contact link is a bonus — don't fail the save if it didn't take.
          console.warn('Failed to attach primary contact to deal:', e);
        }
      }
      onSave();
    } catch (e) {
      console.error(e);
      setSaveError(e?.message || 'Save failed — check backend.log for details.');
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    const text = (form.notes || '').trim();
    if (!text || isNew) return;
    try {
      await api.fetch(`/api/deals/${deal.id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note: text }),
      });
      setForm(f => ({ ...f, notes: '' }));
      await loadActivity();
    } catch (err) {
      console.error('Failed to add note:', err);
      setSaveError('Failed to add note: ' + (err.message || err));
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this deal?')) return;
    try {
      setSaveError(null);
      await api.fetch(`/api/deals/${deal.id}`, { method: 'DELETE' });
      onDelete();
    } catch (e) {
      console.error(e);
      setSaveError('Failed to delete deal: ' + (e.message || e));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-600 rounded-xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto scrollable-content" onClick={e => e.stopPropagation()}>
        {/* Header card. For existing deals we show the company logo + key
            fields up top so the modal feels like a record view, not just a
            form. For new deals we keep the plain title so the picker is the
            first thing the rep sees. */}
        {!isNew ? (
          <div className="mb-5 flex items-start gap-3 p-4 bg-slate-900/60 border border-slate-700 rounded-lg">
            <CompanyLogo company={selectedCompany} size={52} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-base font-semibold text-slate-100 truncate">{form.name || 'Untitled deal'}</div>
                {selectedStage && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: (selectedStage.color || '#475569') + '33', color: selectedStage.color || '#94a3b8' }}>
                    {selectedStage.label}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span>{selectedCompany?.name || 'No company'}</span>
                {selectedCompany?.website && (
                  <>
                    <span className="text-slate-700">·</span>
                    <a href={selectedCompany.website.startsWith('http') ? selectedCompany.website : `https://${selectedCompany.website}`}
                       target="_blank" rel="noopener noreferrer"
                       className="hover:text-indigo-300 underline-offset-2 hover:underline">
                      {selectedCompany.website.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                    </a>
                  </>
                )}
                {selectedCompany?.linkedin_url && (
                  <>
                    <span className="text-slate-700">·</span>
                    <a href={selectedCompany.linkedin_url} target="_blank" rel="noopener noreferrer"
                       className="hover:text-indigo-300 underline-offset-2 hover:underline">LinkedIn</a>
                  </>
                )}
              </div>
              <div className="flex items-baseline gap-4 mt-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                  <div className="text-sm text-slate-100 font-medium">{form.amount ? fmt(form.amount) : '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Close</div>
                  <div className="text-sm text-slate-100">{form.close_date || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Owner</div>
                  <div className="text-sm text-slate-100">{form.owner || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Source</div>
                  <div className="text-sm text-slate-100">{form.source || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <h3 className="text-lg font-bold text-slate-100 mb-4">New Deal</h3>
        )}

        <div className="space-y-3">
          {/* Company picker — typeahead with inline "+ Add". Shown before
              the deal name so reps anchor on the company first. */}
          <div>
            <label className="text-xs text-slate-400">Company</label>
            <div className="mt-1">
              <EntityPicker
                value={selectedCompany}
                placeholder="Search companies…"
                search={async (q) => api.searchCompanies(q, { limit: 8 })}
                onSelect={(c) => {
                  setSelectedCompany(c);
                  // Picking a different company invalidates the contact —
                  // their old contact probably doesn't work at the new org.
                  if (selectedContact) {
                    setSelectedContact(null);
                  }
                }}
                onCreate={async (name) => {
                  const r = await api.createCompanyQuick({ name });
                  return { id: r.id, name: r.name, website: null, linkedin_url: null };
                }}
                renderRow={(c) => (
                  <div className="flex items-center gap-2.5">
                    <CompanyLogo company={c} size={28} />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100 truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {[c.website && c.website.replace(/^https?:\/\//, '').replace(/^www\./, ''), c.industry, c.size].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                  </div>
                )}
                renderSelected={(c) => (
                  <div className="flex items-center gap-2.5">
                    <CompanyLogo company={c} size={28} />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100 truncate">{c.name}</div>
                      {(c.website || c.industry) && (
                        <div className="text-[11px] text-slate-500 truncate">
                          {[c.website && c.website.replace(/^https?:\/\//, '').replace(/^www\./, ''), c.industry].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                addLabel={(n) => `Add new company "${n}"`}
              />
            </div>
          </div>

          {/* Primary contact picker — scoped to the selected company. Lets
              the rep pick an existing lead at that org, or quick-create a
              new one inline. */}
          {selectedCompany && (
            <div>
              <label className="text-xs text-slate-400">Primary contact</label>
              <div className="mt-1">
                <EntityPicker
                  key={selectedCompany.id /* reset query when company changes */}
                  value={selectedContact}
                  placeholder={`Search people at ${selectedCompany.name}…`}
                  search={async (q) => api.leadsAtCompany(selectedCompany.id, { q, limit: 15 })}
                  onSelect={(c) => setSelectedContact(c)}
                  onCreate={async (name) => {
                    const r = await api.createLeadQuick({ full_name: name, company_id: selectedCompany.id });
                    return { id: r.id, full_name: r.full_name, title: r.title, email: r.email, linkedin_url: r.linkedin_url };
                  }}
                  renderRow={(c) => (
                    <div>
                      <div className="text-sm text-slate-100">{c.full_name || c.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {[c.title, c.email].filter(Boolean).join(' · ') || 'no details yet'}
                      </div>
                    </div>
                  )}
                  renderSelected={(c) => (
                    <div>
                      <div className="text-sm text-slate-100">{c.full_name || c.name}</div>
                      {c.title && <div className="text-[11px] text-slate-500">{c.title}</div>}
                    </div>
                  )}
                  addLabel={(n) => `Add new contact "${n}" at ${selectedCompany.name}`}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Suggestions come from leads scraped for this company.
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-400">Deal Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
              className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 focus:ring-1 focus:ring-indigo-500 outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Stage</label>
              <select value={form.stage} onChange={e => setForm(f => ({...f, stage: e.target.value}))}
                className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none">
                {stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Amount</label>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none" placeholder="0" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Weight %</label>
            <input
              type="number" min="0" max="100"
              value={form.weight}
              onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
              placeholder={`stage default: ${selectedStage?.probability ?? 0}%`}
              className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none"
              title="Close probability for the weighted forecast. Leave blank to use the stage default."
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Overrides the stage's default probability in the weighted pipeline. Clear to fall back to the stage default.
            </div>
          </div>

          {showLossReason && (
            <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3">
              <label className="text-xs font-medium text-red-300">Closed-lost reason *</label>
              <select
                value={form.loss_reason_id}
                onChange={e => setForm(f => ({ ...f, loss_reason_id: e.target.value }))}
                className="w-full mt-1 bg-slate-900 border border-red-700/50 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none"
              >
                <option value="">— pick a reason —</option>
                {lostReasons.map(r => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
              <div className="text-[11px] text-red-300/70 mt-1">
                To keep the deal alive instead of closing it, move it to the <span className="font-medium text-sky-300">Nurture</span> stage.
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Close Date</label>
              <input type="date" value={form.close_date} onChange={e => setForm(f => ({...f, close_date: e.target.value}))}
                className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Owner</label>
              <select
                value={form.owner}
                onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none"
              >
                <option value="">— unassigned —</option>
                {users.filter(u => u.active).map(u => (
                  <option key={u.id} value={u.email}>
                    {u.name && u.name !== u.email ? `${u.name} (${u.email})` : u.email}
                  </option>
                ))}
                {/* Show the current owner even if it's a legacy free-text
                    value not in the users table, so we don't silently wipe
                    it on edit. */}
                {form.owner && !users.some(u => u.email === form.owner) && (
                  <option value={form.owner}>{form.owner} (legacy)</option>
                )}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400">Source</label>
            <select
              value={form.source}
              onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
              className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none"
            >
              <option value="">— where did this deal come from? —</option>
              {DEAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              {/* Preserve a legacy / custom value not in the pick-list. */}
              {form.source && !DEAL_SOURCES.includes(form.source) && (
                <option value={form.source}>{form.source}</option>
              )}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-slate-400">Notes / Activity Log</label>
              {!isNew && (
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={!form.notes || !form.notes.trim()}
                    className="text-xs px-2 py-0.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded disabled:opacity-40"
                    title="Add a note to the activity feed — counts toward the Outreach metric in Reporting"
                  >
                    + Log Note
                  </button>
                </div>
              )}
            </div>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={isNew ? 'Notes about this deal…' : 'Add a new note, then click "Log Note" to append with a timestamp.'}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none resize-none"
            />
            {!isNew && (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 bg-slate-900/50 border border-slate-700 rounded-lg p-2 scrollable-content">
                {activity.length === 0 && !deal?.notes && (
                  <div className="text-xs text-slate-600 italic text-center py-2">
                    No activity yet. Log notes, stage moves, and contact changes will show up here.
                  </div>
                )}
                {activity.map((a, i) => (
                  <div key={a.id || i} className="flex gap-2 text-xs border-l-2 border-indigo-500/50 pl-2 py-0.5">
                    <span className="text-slate-500 flex-shrink-0 font-mono">
                      {(a.created_at || a.timestamp || '').toString().slice(0, 16).replace('T', ' ')}
                    </span>
                    <span className="text-slate-300">
                      {a.description || a.note || a.action || JSON.stringify(a)}
                    </span>
                  </div>
                ))}
                {activity.length === 0 && deal?.notes && (
                  <div className="text-xs text-slate-400 whitespace-pre-wrap">{deal.notes}</div>
                )}
              </div>
            )}
          </div>

          {form.stage === 'closed_lost' && (
            <div>
              <label className="text-xs text-slate-400">Loss Reason</label>
              <input value={form.loss_reason} onChange={e => setForm(f => ({...f, loss_reason: e.target.value}))}
                className="w-full mt-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none" />
            </div>
          )}

          {!isNew && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-400">Contacts</label>
                <button
                  type="button"
                  onClick={() => setShowAddContact(true)}
                  className="text-xs px-2 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {contacts.length === 0 ? (
                  <p className="text-xs text-slate-500">No contacts assigned</p>
                ) : (
                  contacts.map(c => (
                    <div key={c.id} className="p-2 bg-slate-900 border border-slate-700 rounded flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-slate-100">{c.full_name || c.name || 'Unknown'}</div>
                        {c.title && <div className="text-xs text-slate-500">{c.title}</div>}
                        {c.role && <div className="text-xs text-slate-600">{c.role}</div>}
                      </div>
                      <div className="flex gap-1">
                        {!c.is_primary && (
                          <button
                            type="button"
                            onClick={() => handleSetPrimary(c.id)}
                            className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded"
                          >
                            Make Primary
                          </button>
                        )}
                        {c.is_primary && (
                          <span className="text-xs px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded">Primary</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveContact(c.id)}
                          className="text-xs px-2 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {showAddContact && (
                <div className="mt-3 p-3 bg-slate-900 border border-slate-700 rounded-lg">
                  <select
                    value={selectedLeadId}
                    onChange={e => setSelectedLeadId(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-slate-100 outline-none mb-2"
                  >
                    <option value="">Select a contact</option>
                    {leads.map(l => (
                      <option key={l.id} value={l.id}>{l.full_name || l.name} - {l.title || 'N/A'}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddContact}
                      disabled={!selectedLeadId}
                      className="flex-1 text-xs px-2 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded disabled:opacity-50"
                    >
                      Add Contact
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAddContact(false); setSelectedLeadId(''); }}
                      className="flex-1 text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tasks attached to this deal. Showing open + recently-closed
              tasks so the rep sees the cadence at a glance. + Task opens
              the regular task-create modal pre-attached to deal_id. */}
          {!isNew && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-400">
                  Tasks
                  {dealTasks.length > 0 && (
                    <span className="ml-1.5 text-[10px] text-slate-500">
                      ({dealTasks.filter(t => t.status !== 'closed').length} open · {dealTasks.length} total)
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddTask(true)}
                  className="text-xs px-2 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded"
                >
                  + Task
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto scrollable-content">
                {dealTasks.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    No tasks yet. Use <span className="text-slate-300">+ Task</span> to add the next follow-up, demo, send-collateral, etc.
                  </p>
                ) : dealTasks.map(t => {
                  const closed  = t.status === 'closed';
                  const overdue = t.overdue;
                  return (
                    <div key={t.id} className="flex items-center gap-2 p-2 bg-slate-900 border border-slate-700 rounded">
                      <input
                        type="checkbox"
                        checked={closed}
                        onChange={() => handleToggleTask(t)}
                        className="flex-shrink-0"
                        title={closed ? 'Reopen' : 'Mark done'}
                      />
                      <div className="flex-1 min-w-0">
                        <div className={'text-xs truncate ' + (closed ? 'text-slate-500 line-through' : 'text-slate-100')}>
                          {t.name}
                        </div>
                        {t.assignee_name && (
                          <div className="text-[10px] text-slate-500 truncate">{t.assignee_name}</div>
                        )}
                      </div>
                      {t.due_date && (
                        <span className={
                          'text-[10px] tabular-nums flex-shrink-0 ' +
                          (closed   ? 'text-slate-600' :
                           overdue ? 'text-rose-400'  : 'text-slate-500')
                        }>
                          {t.due_date}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {saveError && (
          <div className="mt-4 p-2 bg-red-500/10 border border-red-500/40 rounded text-xs text-red-300">
            {saveError}
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <div>
            {!isNew && (
              <button onClick={handleDelete} className="text-sm text-red-400 hover:text-red-300">Delete Deal</button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {showAddTask && !isNew && (
        <CreateTaskModal
          users={users}
          defaultDealId={deal.id}
          defaultDealName={form.name || deal.name}
          onClose={() => setShowAddTask(false)}
          onCreated={() => { setShowAddTask(false); loadDealTasks(); }}
        />
      )}
    </div>
  );
}

function ForecastBar({ deals, stages }) {
  const wonIds  = stages.filter(s => s.is_won).map(s => s.id);
  const lostIds = stages.filter(s => s.is_lost).map(s => s.id);
  const closed  = new Set([...wonIds, ...lostIds]);
  const active = deals.filter(d => !closed.has(d.stage));
  const won    = deals.filter(d => wonIds.includes(d.stage));
  const lost   = deals.filter(d => lostIds.includes(d.stage));

  const totalPipeline = active.reduce((s, d) => s + (d.amount || 0), 0);
  const weightedPipeline = active.reduce((s, d) => {
    const stage = stages.find(st => st.id === d.stage);
    // Per-deal weight override wins; stage probability is the default.
    return s + ((d.amount || 0) * (d.weight ?? stage?.probability ?? 0) / 100);
  }, 0);
  const wonTotal = won.reduce((s, d) => s + (d.amount || 0), 0);

  // Average deal size — based on won deals so it's grounded in real revenue,
  // not aspirational pipeline. Falls back to active pipeline average if you
  // haven't closed anything yet.
  const sizingPool = won.length ? won : active.filter(d => d.amount);
  const avgDealSize = sizingPool.length
    ? sizingPool.reduce((s, d) => s + (d.amount || 0), 0) / sizingPool.length
    : 0;

  const closedCount = won.length + lost.length;
  const winRatePct = closedCount > 0 ? Math.round((won.length / closedCount) * 100) : null;
  const winRateTooltip = closedCount > 0
    ? `${won.length} won ÷ ${closedCount} closed (won + lost) in the currently loaded deals`
    : 'No closed deals in the current view yet';

  return (
    <div className="flex items-center gap-6 bg-slate-800/50 border border-slate-700 rounded-lg px-5 py-3">
      <div>
        <div className="text-xs text-slate-400">Pipeline</div>
        <div className="text-lg font-bold text-slate-100">{fmt(totalPipeline)}</div>
      </div>
      <div>
        <div className="text-xs text-slate-400">Weighted</div>
        <div className="text-lg font-bold text-indigo-400">{fmt(weightedPipeline)}</div>
      </div>
      <div>
        <div className="text-xs text-slate-400">Won</div>
        <div className="text-lg font-bold text-emerald-400">{fmt(wonTotal)}</div>
      </div>
      <div title={won.length ? 'Average size of won deals' : 'Average size of active deals (no wins yet)'}>
        <div className="text-xs text-slate-400">Avg deal</div>
        <div className="text-lg font-bold text-slate-100">{avgDealSize ? fmt(avgDealSize) : '—'}</div>
      </div>
      <div>
        <div className="text-xs text-slate-400">Deals</div>
        <div className="text-lg font-bold text-slate-100">{deals.length}</div>
      </div>
      <div title={winRateTooltip}>
        <div className="text-xs text-slate-400">Win Rate</div>
        <div className="text-lg font-bold text-amber-400">
          {winRatePct !== null ? winRatePct + '%' : '—'}
        </div>
      </div>
    </div>
  );
}

export default function DealsPanel() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [view, setView] = useState('kanban'); // kanban | list
  const scrollRef = useRef(null);

  // Dynamic stage list (was a module-level const). Falls back to seed list
  // until the API responds so the kanban renders something immediately.
  const [stages, setStages]             = useState(FALLBACK_STAGES);
  const [lostReasons, setLostReasons]   = useState([]);
  const [users, setUsers]               = useState([]);

  // Confirmation modal for stage transitions via drag-drop.
  // Holds the pending move; cleared once the user confirms or cancels.
  const [pendingMove, setPendingMove] = useState(null);
    // shape: { dealId, fromStage, toStage, lossReasonId? }

  useEffect(() => {
    api.listDealStages().then(setStages).catch(() => setStages(FALLBACK_STAGES));
    api.listLostReasons().then(setLostReasons).catch(() => setLostReasons([]));
    api.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  // ---- Filters (kanban-wide) ----
  // globalQuery: searches every column (name / company / contact / owner / notes).
  // ownerFilter: restricts to one rep ('' = all reps).
  // collapsedStages: set of stage ids currently collapsed.
  // sortKey:  'newest' (default), 'oldest', or 'amount'.
  // Persist UI state to localStorage so reps don't lose their layout on reload.
  const [globalQuery, setGlobalQuery]     = useState('');
  const [ownerFilter, setOwnerFilter]     = useState('');
  const [collapsedStages, setCollapsedStages] = useState(() => {
    try {
      const raw = localStorage.getItem('deals_collapsed_stages');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [sortKey, setSortKey] = useState(() => localStorage.getItem('deals_sort_key') || 'newest');
  const [hideClosed, setHideClosed] = useState(() => localStorage.getItem('deals_hide_closed') !== 'false');
  // Show Nurture defaults OFF — deals parked there are intentionally
  // shelved and would otherwise clutter the active-pipeline view. Flip
  // the toggle to work the nurture cadence.
  const [showNurture, setShowNurture] = useState(
    () => localStorage.getItem('deals_show_nurture') === 'true',
  );

  useEffect(() => {
    localStorage.setItem('deals_collapsed_stages', JSON.stringify([...collapsedStages]));
  }, [collapsedStages]);
  useEffect(() => { localStorage.setItem('deals_sort_key', sortKey); }, [sortKey]);
  useEffect(() => { localStorage.setItem('deals_hide_closed', String(hideClosed)); }, [hideClosed]);
  useEffect(() => { localStorage.setItem('deals_show_nurture', String(showNurture)); }, [showNurture]);

  const toggleCollapse = (stageId) => setCollapsedStages(prev => {
    const next = new Set(prev);
    if (next.has(stageId)) next.delete(stageId); else next.add(stageId);
    return next;
  });

  // Filtered list passed to columns. Per-column search still runs on top.
  const filteredDeals = React.useMemo(() => {
    let out = deals;
    if (ownerFilter) out = out.filter(d => d.owner === ownerFilter);
    if (globalQuery.trim()) {
      const q = globalQuery.trim().toLowerCase();
      out = out.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.company_name || '').toLowerCase().includes(q) ||
        (d.contact_name || '').toLowerCase().includes(q) ||
        (d.owner || '').toLowerCase().includes(q) ||
        (d.notes || '').toLowerCase().includes(q));
    }
    return out;
  }, [deals, ownerFilter, globalQuery]);

  const owners = React.useMemo(
    () => [...new Set(deals.map(d => d.owner).filter(Boolean))].sort(),
    [deals],
  );

  // Map email -> friendly display name. The kanban cards + list view + filter
  // dropdown all want "Aidan" instead of "aidan@…" once admins set a name.
  const ownerLabel = React.useMemo(() => {
    const m = {};
    for (const u of users) {
      if (u.email && u.name && u.name !== u.email) {
        m[u.email] = u.name;
      }
    }
    return (email) => (email && m[email]) || email || '';
  }, [users]);

  // visibleStages: drop closed stages when hideClosed is on, and drop the
  // nurture column unless the rep has explicitly opted into seeing it.
  // We keep the column itself in the data (so deals there still load) — we
  // just don't render it.
  const visibleStages = stages.filter(s => {
    if (hideClosed && (s.is_won || s.is_lost)) return false;
    if (!showNurture && s.id === 'nurture')    return false;
    return true;
  });

  const loadDeals = useCallback(async () => {
    try {
      const data = await api.fetch('/api/deals');
      setDeals(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  // Drag-drop into a column doesn't apply immediately anymore. We open a
  // confirmation modal first so reps don't accidentally toss a deal into
  // closed_won by sneezing. Real PUT happens in confirmStageMove() below.
  const handleDrop = (dealId, newStage) => {
    const d = deals.find(x => x.id === dealId);
    if (!d || d.stage === newStage) return;
    setPendingMove({
      dealId,
      fromStage: d.stage,
      toStage:   newStage,
      lossReasonId: '',   // filled in if user picks one
      dealName:  d.name,
    });
  };

  const cancelStageMove = () => setPendingMove(null);

  const confirmStageMove = async () => {
    if (!pendingMove) return;
    const { dealId, toStage, lossReasonId } = pendingMove;
    const targetStage = stages.find(s => s.id === toStage);
    if (targetStage?.is_lost && !lossReasonId) {
      // Inline guard — the modal also disables the button, but double-check.
      return;
    }
    // Optimistic update.
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: toStage } : d));
    setPendingMove(null);
    try {
      const body = { stage: toStage };
      if (lossReasonId) body.loss_reason_id = lossReasonId;
      await api.fetch(`/api/deals/${dealId}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      // Refresh so closed_lost / stage-driven side effects show up.
      loadDeals();
    } catch (e) {
      console.error(e);
      loadDeals();
    }
  };

  const openEdit = (deal) => { setEditDeal(deal); setShowModal(true); };
  const openNew = () => { setEditDeal(null); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditDeal(null); };
  const afterSave = () => { closeModal(); loadDeals(); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Deal Pipeline</h2>
          <p className="text-sm text-slate-400 mt-0.5">Drag deals between stages to update</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-800 rounded-lg border border-slate-600 overflow-hidden">
            <button onClick={() => setView('kanban')}
              className={`px-3 py-1.5 text-xs font-medium ${view === 'kanban' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
              Board
            </button>
            <button onClick={() => setView('list')}
              className={`px-3 py-1.5 text-xs font-medium ${view === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
              List
            </button>
          </div>
          <button onClick={openNew}
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg">
            + New Deal
          </button>
        </div>
      </div>

      {/* Forecast */}
      <div className="flex-shrink-0 mb-4">
        <ForecastBar deals={deals} stages={stages} />
      </div>

      {/* Filter bar */}
      <div className="flex-shrink-0 mb-3 flex items-center gap-2 text-xs flex-wrap">
        <input
          value={globalQuery}
          onChange={(e) => setGlobalQuery(e.target.value)}
          placeholder="Search deals…"
          className="bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500 w-64"
        />
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200"
        >
          <option value="">All owners</option>
          {owners.map(o => <option key={o} value={o}>{ownerLabel(o)}</option>)}
        </select>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200"
        >
          <option value="newest">Sort: most recent activity</option>
          <option value="oldest">Sort: needs follow-up</option>
          <option value="amount">Sort: deal size</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-slate-300 ml-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideClosed}
            onChange={(e) => setHideClosed(e.target.checked)}
          />
          Hide closed
        </label>
        <label
          className="inline-flex items-center gap-1.5 text-slate-300 cursor-pointer"
          title="Show the Nurture column — long-cycle deals being kept warm. Off by default to keep the active board focused."
        >
          <input
            type="checkbox"
            checked={showNurture}
            onChange={(e) => setShowNurture(e.target.checked)}
          />
          Show nurture
        </label>
        {(globalQuery || ownerFilter) && (
          <button
            onClick={() => { setGlobalQuery(''); setOwnerFilter(''); }}
            className="text-slate-400 hover:text-slate-200 underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Kanban / List */}
      {view === 'kanban' ? (
        // overscroll-x: contain stops the browser's back-gesture from
        // triggering when the rep drags past the leftmost column on a
        // trackpad — that was the "stuck on the left" feel. Tiny left/right
        // padding gives the first/last column some breathing room so they
        // don't look pinned to the edge.
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden scrollable-content"
          style={{ overscrollBehaviorX: 'contain' }}
        >
          <div className="flex gap-3 h-full pb-4 px-1">
            {visibleStages.map(stage => (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                deals={filteredDeals}
                onEdit={openEdit}
                onDrop={handleDrop}
                dragOverStage={dragOverStage}
                setDragOverStage={setDragOverStage}
                collapsed={collapsedStages.has(stage.id)}
                onToggleCollapse={toggleCollapse}
                sortKey={sortKey}
                ownerLabel={ownerLabel}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto scrollable-content">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-700">
                <th className="pb-2 px-3">Deal</th>
                <th className="pb-2 px-3">Company</th>
                <th className="pb-2 px-3">Stage</th>
                <th className="pb-2 px-3">Amount</th>
                <th className="pb-2 px-3">Close Date</th>
                <th className="pb-2 px-3">Owner</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(d => {
                const stage = stages.find(s => s.id === d.stage);
                return (
                  <tr key={d.id} onClick={() => openEdit(d)}
                    className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer text-sm">
                    <td className="py-2.5 px-3 text-slate-100 font-medium">{d.name}</td>
                    <td className="py-2.5 px-3 text-slate-400">{d.company_name || '—'}</td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: stage?.color + '22', color: stage?.color }}>
                        {stage?.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-emerald-400 font-medium">{fmt(d.amount)}</td>
                    <td className="py-2.5 px-3 text-slate-500">{d.close_date?.split('T')[0] || '—'}</td>
                    <td className="py-2.5 px-3 text-slate-400">{d.owner ? ownerLabel(d.owner) : '—'}</td>
                  </tr>
                );
              })}
              {deals.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No deals yet. Click "+ New Deal" to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <DealModal
          deal={editDeal}
          stages={stages}
          lostReasons={lostReasons}
          users={users}
          onClose={closeModal}
          onSave={afterSave}
          onDelete={afterSave}
        />
      )}

      {pendingMove && (
        <StageMoveConfirm
          pending={pendingMove}
          setPending={setPendingMove}
          stages={stages}
          lostReasons={lostReasons}
          onConfirm={confirmStageMove}
          onCancel={cancelStageMove}
        />
      )}
    </div>
  );
}


function StageMoveConfirm({ pending, setPending, stages, lostReasons, onConfirm, onCancel }) {
  const fromLabel = stages.find(s => s.id === pending.fromStage)?.label || pending.fromStage;
  const toStage   = stages.find(s => s.id === pending.toStage);
  const toLabel   = toStage?.label || pending.toStage;
  const isLost    = !!toStage?.is_lost;
  const isWon     = !!toStage?.is_won;
  const needsReason = isLost && !pending.lossReasonId;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-100 mb-2">Move deal to {toLabel}?</h3>
        <p className="text-sm text-slate-400 mb-4">
          <span className="text-slate-300">{pending.dealName}</span>
          <br />
          <span className="text-xs">{fromLabel} → <span className={isWon ? 'text-emerald-400' : isLost ? 'text-red-400' : ''}>{toLabel}</span></span>
        </p>

        {isLost && (
          <div className="mb-4">
            <label className="text-xs font-medium text-red-300 block mb-1">Closed-lost reason</label>
            <select
              value={pending.lossReasonId}
              onChange={(e) => setPending({ ...pending, lossReasonId: e.target.value })}
              className="w-full bg-slate-800 border border-red-700/50 rounded px-3 py-2 text-sm text-slate-100"
            >
              <option value="">— pick a reason —</option>
              {lostReasons.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-red-300/70 mt-1">
              To keep the deal alive, cancel and move it to the <span className="font-medium text-sky-300">Nurture</span> stage instead.
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 text-sm text-slate-300 border border-slate-700 hover:bg-slate-800 rounded"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={needsReason}
            className={
              'flex-1 px-3 py-2 text-sm font-medium rounded transition-colors ' +
              (needsReason
                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                : isWon
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : isLost
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-indigo-600 hover:bg-indigo-500 text-white')
            }
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
